import { useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import { getItemPrepFloor } from '../../prepItemFloors'
import {
  INextWeekPrepItemRequirementsType,
  INextWeekPrepItemRequirementsVariables,
  INextWeekTotalSalesForecastType,
  INextWeekTotalSalesForecastVariables,
  IPrepItemBatchStandardsType,
  IPrepItemCategoryType,
  IPrepItemSettingsType,
  IPrepItemWeekdayConfig,
  IPrepItemWeekdayConfig2Type,
  IPrepSettingsByDateType,
  IPrepSettingsByDateVariables,
  IStorePrepScheduleCalcType,
  getNextWeekPrepItemRequirementsByDow,
  getNextWeekTotalSalesForecast,
  getPrepItemBatchStandards,
  getPrepItemCategory,
  getPrepItemSettings,
  getPrepItemWeekdayConfig2,
  getPrepSettingsByDate,
  getStorePrepScheduleForCalc,
} from '../gql'
import { calculateDaysUntilNextPrep } from '../utils/prepItemCalculations'

// Helper function to calculate unit amount for the selected day only
const calculateTodayUnitAmount = (
  prepItem: IPrepItemWeekdayConfig,
  requirements: Array<{
    prepItemIdCode: string
    forecastDate: string
    unitAmountOfEachPrepItemNeeded: number
  }>,
  currentDate: moment.Moment,
): number => {
  // Calculate only today's requirements sum
  const todaySum = requirements
    .filter(
      (req) =>
        req.prepItemIdCode === prepItem.prepItemIdCode &&
        req.forecastDate === currentDate.format('YYYY-MM-DD'),
    )
    .reduce((sum, req) => sum + req.unitAmountOfEachPrepItemNeeded, 0)

  return todaySum
}

// Helper function to calculate total unit amount from today until next prep day
// Round-then-sum: rounds each day to 0.1 precision before summing
const calculateTotalUnitAmountUntilNextPrep = (
  prepItem: IPrepItemWeekdayConfig,
  requirements: Array<{
    prepItemIdCode: string
    forecastDate: string
    unitAmountOfEachPrepItemNeeded: number
  }>,
  currentDate: moment.Moment,
  daysUntilNextPrep: number,
  standardUnitsPerBatch: number,
): number => {
  // Sum requirements from today through (daysUntilNextPrep - 1) days ahead
  let totalSum = 0
  for (let i = 0; i < daysUntilNextPrep; i++) {
    const checkDate = currentDate.clone().add(i, 'days')
    const daySum = requirements
      .filter(
        (req) =>
          req.prepItemIdCode === prepItem.prepItemIdCode &&
          req.forecastDate === checkDate.format('YYYY-MM-DD'),
      )
      .reduce((sum, req) => sum + req.unitAmountOfEachPrepItemNeeded, 0)

    const dayNeeded = daySum / standardUnitsPerBatch
    const roundedDayNeeded = Math.ceil(dayNeeded * 10) / 10
    totalSum += roundedDayNeeded * standardUnitsPerBatch
  }

  return totalSum
}

const usePrepItemWeekdayDataSource = (
  locationId: number,
  date: moment.Moment | null,
  deliveryDayNumbers: Set<number> = new Set(),
  deliverySafeItemIds: Set<string> = new Set(),
  redistributionEnabled = false,
) => {
  // Format date for the query (user selected date)
  const queryDate = date ? date.format('YYYY-MM-DD') : null

  // Use store_prep_schedule when locationId is available (full fallback chain built in)
  const { data: storePrepScheduleData, loading: storePrepScheduleLoading } =
    useQuery<IStorePrepScheduleCalcType>(getStorePrepScheduleForCalc, {
      variables: { iQueryDate: queryDate, iLocationId: locationId || 0 },
      skip: !queryDate || !locationId,
    })

  // config_2 fallback (used if store_prep_schedule returns empty)
  const {
    data: prepItemWeekdayConfig2Data,
    loading: prepItemWeekdayConfig2Loading,
  } = useQuery<IPrepItemWeekdayConfig2Type>(getPrepItemWeekdayConfig2, {
    variables: { iQueryDate: queryDate, iLocationId: locationId || 0 },
    skip:
      !queryDate ||
      !locationId ||
      (storePrepScheduleData?.getStorePrepSchedule?.nodes?.length ?? 0) > 0,
  })

  // Priority: store_prep_schedule > config_2
  const effectiveWeekdayConfig = useMemo(() => {
    if (
      locationId &&
      storePrepScheduleData?.getStorePrepSchedule?.nodes?.length
    ) {
      return storePrepScheduleData.getStorePrepSchedule.nodes
    }
    // why: test the nodes, not the response object. A successful query that
    // matched nothing still returns an object, so the object test ended the
    // cascade with an empty grid instead of falling through — the opposite of
    // what this chain is for.
    if (
      locationId &&
      prepItemWeekdayConfig2Data?.getPrepItemWeekdayConfig2?.nodes?.length
    ) {
      return prepItemWeekdayConfig2Data.getPrepItemWeekdayConfig2.nodes
    }
    return []
  }, [locationId, storePrepScheduleData, prepItemWeekdayConfig2Data])

  const weekdayConfigLoading =
    prepItemWeekdayConfig2Loading || storePrepScheduleLoading
  const {
    data: nextWeekPrepItemRequirementsData,
    loading: nextWeekPrepItemRequirementsLoading,
  } = useQuery<
    INextWeekPrepItemRequirementsType,
    INextWeekPrepItemRequirementsVariables
  >(getNextWeekPrepItemRequirementsByDow, {
    variables: {
      locationId,
    },
    skip: !locationId,
  })
  const {
    data: prepItemBatchStandardsData,
    loading: prepItemBatchStandardsLoading,
  } = useQuery<IPrepItemBatchStandardsType>(getPrepItemBatchStandards)
  const { data: prepItemSettingsData, loading: prepItemSettingsLoading } =
    useQuery<IPrepItemSettingsType>(getPrepItemSettings)
  const { data: prepItemCategoryData, loading: prepItemCategoryLoading } =
    useQuery<IPrepItemCategoryType>(getPrepItemCategory)
  const {
    data: nextWeekTotalSalesForecastData,
    loading: nextWeekTotalSalesForecastLoading,
  } = useQuery<
    INextWeekTotalSalesForecastType,
    INextWeekTotalSalesForecastVariables
  >(getNextWeekTotalSalesForecast, {
    variables: {
      locationId,
      queryDate: date?.format('YYYY-MM-DD') || '',
    },
    skip: !locationId || !date,
  })
  const { data: prepSettingsByDateData, loading: prepSettingsByDateLoading } =
    useQuery<IPrepSettingsByDateType, IPrepSettingsByDateVariables>(
      getPrepSettingsByDate,
      {
        variables: {
          iQueryDate: date?.format('YYYY-MM-DD') || '',
          iLocationId: locationId as number,
        },
        // why: the increase can be scoped to a store's group, so asking without
        // a store would return the company value and pre-fill the wrong number.
        skip: !date || !locationId,
      },
    )

  // Global adjustment percent from prep settings. Surfaced to the page as the
  // default value for the visible Sales Forecast Adjustment field; no longer
  // applied as a separate prep-layer multiplier.
  const globalAdjustmentPercent = useMemo(() => {
    const setting = prepSettingsByDateData?.getPrepSettingsByDate?.nodes?.find(
      (s) => s.settingName === 'Prep Amount Increase Percent',
    )
    return setting?.settingValue || 0
  }, [prepSettingsByDateData])

  const dataSource = useMemo(() => {
    if (
      !date ||
      effectiveWeekdayConfig.length === 0 ||
      !nextWeekPrepItemRequirementsData?.getNextWeekPrepItemRequirementsByDow ||
      !prepItemBatchStandardsData?.getPrepItemBatchStandards ||
      !prepItemSettingsData?.getPrepItemSettings ||
      !prepItemCategoryData?.getPrepItemCategory
    )
      return []

    // Check if selected date is later than the forecastDate
    const forecastDates =
      nextWeekPrepItemRequirementsData.getNextWeekPrepItemRequirementsByDow.nodes
        .map((node) => node.forecastDate)
        .filter((date) => date)

    if (forecastDates.length > 0) {
      const maxForecastDate = moment.max(forecastDates.map((d) => moment(d)))
      const minForecastDate = moment.min(forecastDates.map((d) => moment(d)))
      if (date.isAfter(maxForecastDate) || date.isBefore(minForecastDate)) {
        return []
      }
    }

    const weekdayStr = date.format('dddd').toLowerCase()
    // why: when redistribution is on and today is a delivery day, drop non-safe
    // items from the prep list — the team is told not to prep them today and
    // their coverage is absorbed by the prior prep day.
    const todayIsDelivery =
      redistributionEnabled && deliveryDayNumbers.has(date.day())

    const itemsPrepToday = effectiveWeekdayConfig.filter((node) => {
      if (
        !node[weekdayStr as keyof IPrepItemWeekdayConfig] ||
        !node.isActivated
      )
        return false
      if (todayIsDelivery && !deliverySafeItemIds.has(node.prepItemIdCode))
        return false
      return true
    })

    // Create the result with calculated totals
    const results = itemsPrepToday.map((prepItem) => {
      // Find one requirement record to get the unit info
      const sampleRequirement =
        nextWeekPrepItemRequirementsData.getNextWeekPrepItemRequirementsByDow.nodes.find(
          (req) => req.prepItemIdCode === prepItem.prepItemIdCode,
        )
      const prepDetails =
        prepItemBatchStandardsData?.getPrepItemBatchStandards.nodes
          .filter((n) => n.sourceType === 'PREP')
          .find((node) => node.prepItemIdCode === prepItem.prepItemIdCode)

      // Get settings for this item (restDays and displayOnHandBatchType)
      const itemSettings = prepItemSettingsData?.getPrepItemSettings.nodes.find(
        (setting) => setting.prepItemIdCode === prepItem.prepItemIdCode,
      )

      const restDays = itemSettings?.restDays || 0
      const minimumUnit = itemSettings?.minimumUnit || 0
      const prepOnHandDetails =
        prepItemBatchStandardsData?.getPrepItemBatchStandards.nodes
          .filter(
            (n) =>
              n.sourceType === 'ONHAND' &&
              n.prepItemIdCode === prepItem.prepItemIdCode,
          )
          .map((n) => ({
            prepBatchType: n.prepBatchType,
            standardUnitsPerBatch: n.standardUnitsPerBatch,
          }))

      // Find the category for this item
      const category = prepItemCategoryData?.getPrepItemCategory.nodes.find(
        (cat) => cat.prepItemIdCode === prepItem.prepItemIdCode,
      )

      // Get onHandUnit from settings
      const onHandUnit = itemSettings?.displayOnHandBatchType
        ? (() => {
            const onHandBatchStandard =
              prepItemBatchStandardsData?.getPrepItemBatchStandards.nodes.find(
                (n) =>
                  n.prepItemIdCode === prepItem.prepItemIdCode &&
                  n.batchTypeId === itemSettings.displayOnHandBatchTypeId,
              )
            return onHandBatchStandard
              ? {
                  type: onHandBatchStandard.prepBatchType,
                  standardUnitPerUnit:
                    onHandBatchStandard.standardUnitsPerBatch,
                }
              : null
          })()
        : null

      // Calculate days until next prep for this item
      const weekdayConfig = {
        sunday: prepItem.sunday,
        monday: prepItem.monday,
        tuesday: prepItem.tuesday,
        wednesday: prepItem.wednesday,
        thursday: prepItem.thursday,
        friday: prepItem.friday,
        saturday: prepItem.saturday,
      }
      // why: per-store prep schedule grid already reflects the 4-rule
      // combination algorithm (Rule 2 shifts non-safe items to the day
      // before each delivery). The calculator just sums forecast until the
      // next prep day — no runtime redistribution needed.
      const daysUntilNextPrep = calculateDaysUntilNextPrep(
        date.clone(),
        weekdayConfig,
      )

      // Calculate today's requirement and total requirement until next prep
      const todayUnitAmount = calculateTodayUnitAmount(
        prepItem,
        nextWeekPrepItemRequirementsData.getNextWeekPrepItemRequirementsByDow
          .nodes,
        date.clone(),
      )

      // Calculate total with restDays included: daysUntilNextPrep + restDays
      const totalDaysToPrep = daysUntilNextPrep + restDays
      const totalUnitAmountUntilNextPrep =
        calculateTotalUnitAmountUntilNextPrep(
          prepItem,
          nextWeekPrepItemRequirementsData.getNextWeekPrepItemRequirementsByDow
            .nodes,
          date.clone(),
          totalDaysToPrep,
          prepDetails?.standardUnitsPerBatch || 0,
        )

      return {
        prepItemIdCode: prepItem.prepItemIdCode,
        prepItemName: prepItem.prepItemName,
        prepItemUnit: sampleRequirement?.prepItemUnit || '',
        unitAmountNeeded: todayUnitAmount,
        totalUnitAmountUntilNextPrep,
        prepBatchType: prepDetails?.prepBatchType || '',
        standardUnitsPerBatch: prepDetails?.standardUnitsPerBatch || 0,
        prepOnHandDetails,
        parentPrepCategoryIdCode: category?.parentPrepCategoryIdCode || '',
        onHandUnit,
        restDays,
        minimumUnit,
        prepFloorPercent: getItemPrepFloor(prepItem.prepItemIdCode),
      }
    })

    // Sort by category to group items with same category together
    return results.sort((a, b) => {
      // First sort by category
      if (a.parentPrepCategoryIdCode !== b.parentPrepCategoryIdCode) {
        return a.parentPrepCategoryIdCode.localeCompare(
          b.parentPrepCategoryIdCode,
        )
      }
      // Then sort by item name within the same category
      return a.prepItemName.localeCompare(b.prepItemName)
    })
  }, [
    effectiveWeekdayConfig,
    date,
    nextWeekPrepItemRequirementsData,
    prepItemCategoryData,
    prepItemBatchStandardsData,
    prepItemSettingsData,
    deliveryDayNumbers,
    deliverySafeItemIds,
    redistributionEnabled,
  ])

  const totalSalesForecast = useMemo(() => {
    if (
      !date ||
      !nextWeekPrepItemRequirementsData?.getNextWeekPrepItemRequirementsByDow
    ) {
      return 0
    }

    // Check if selected date is later than the forecastDate
    const forecastDates =
      nextWeekPrepItemRequirementsData.getNextWeekPrepItemRequirementsByDow.nodes
        .map((node) => node.forecastDate)
        .filter((date) => date)

    if (forecastDates.length > 0) {
      const maxForecastDate = moment.max(forecastDates.map((d) => moment(d)))
      if (date.isAfter(maxForecastDate)) {
        return 0
      }
    }

    const currentDateStr = date.format('YYYY-MM-DD')
    const todayRequirements =
      nextWeekPrepItemRequirementsData.getNextWeekPrepItemRequirementsByDow.nodes.filter(
        (req) => req.forecastDate === currentDateStr,
      )

    return Math.max(...todayRequirements.map((req) => req.salesForecast), 0)
  }, [nextWeekPrepItemRequirementsData, date])

  const weeklySalesForecast = useMemo(() => {
    // why: the guard has to reach the row, not just the response. A store with
    // no sales forecast for the current business week — a new store, or a week
    // the forecast has not landed for — returns an empty node list, which
    // satisfies an object check and then throws on nodes[0].
    const forecast =
      nextWeekTotalSalesForecastData?.getNextWeekTotalSalesForecast?.nodes?.[0]

    if (!date || !forecast) {
      return 0
    }

    return forecast.weeklySalesForecast
  }, [nextWeekTotalSalesForecastData, date])

  const nonPrepDataSource = useMemo(() => {
    if (
      !date ||
      effectiveWeekdayConfig.length === 0 ||
      !nextWeekPrepItemRequirementsData?.getNextWeekPrepItemRequirementsByDow ||
      !prepItemBatchStandardsData?.getPrepItemBatchStandards ||
      !prepItemSettingsData?.getPrepItemSettings ||
      !prepItemCategoryData?.getPrepItemCategory
    )
      return []

    const weekdayStr = date.format('dddd').toLowerCase()
    // why: items hidden from `dataSource` because they're non-safe on a delivery
    // day still need to surface here so the team can record on-hand counts and
    // get an alert if they're at risk of running out (per Nikki's 4/23 reply).
    const todayIsDelivery =
      redistributionEnabled && deliveryDayNumbers.has(date.day())

    // Get items NOT scheduled for today but are activated
    const itemsNotPrepToday = effectiveWeekdayConfig.filter((node) => {
      if (!node.isActivated) return false
      const isPrepToday = node[weekdayStr as keyof IPrepItemWeekdayConfig]
      if (!isPrepToday) return true
      // Prep day, but suppressed by delivery-day rule → show as non-prep
      if (todayIsDelivery && !deliverySafeItemIds.has(node.prepItemIdCode))
        return true
      return false
    })

    // Map non-prep items with calculations (still need to track inventory)
    const results = itemsNotPrepToday.map((prepItem) => {
      const sampleRequirement =
        nextWeekPrepItemRequirementsData.getNextWeekPrepItemRequirementsByDow.nodes.find(
          (req) => req.prepItemIdCode === prepItem.prepItemIdCode,
        )
      const prepDetails =
        prepItemBatchStandardsData?.getPrepItemBatchStandards.nodes
          .filter((n) => n.sourceType === 'PREP')
          .find((node) => node.prepItemIdCode === prepItem.prepItemIdCode)

      // Get settings for this item (restDays and displayOnHandBatchType)
      const itemSettings = prepItemSettingsData?.getPrepItemSettings.nodes.find(
        (setting) => setting.prepItemIdCode === prepItem.prepItemIdCode,
      )

      const restDays = itemSettings?.restDays || 0
      const minimumUnit = itemSettings?.minimumUnit || 0
      const prepOnHandDetails =
        prepItemBatchStandardsData?.getPrepItemBatchStandards.nodes
          .filter(
            (n) =>
              n.sourceType === 'ONHAND' &&
              n.prepItemIdCode === prepItem.prepItemIdCode,
          )
          .map((n) => ({
            prepBatchType: n.prepBatchType,
            standardUnitsPerBatch: n.standardUnitsPerBatch,
          }))

      const category = prepItemCategoryData?.getPrepItemCategory.nodes.find(
        (cat) => cat.prepItemIdCode === prepItem.prepItemIdCode,
      )

      // Get onHandUnit from settings
      const onHandUnit = itemSettings?.displayOnHandBatchType
        ? (() => {
            const onHandBatchStandard =
              prepItemBatchStandardsData?.getPrepItemBatchStandards.nodes.find(
                (n) =>
                  n.prepItemIdCode === prepItem.prepItemIdCode &&
                  n.batchTypeId === itemSettings.displayOnHandBatchTypeId,
              )
            return onHandBatchStandard
              ? {
                  type: onHandBatchStandard.prepBatchType,
                  standardUnitPerUnit:
                    onHandBatchStandard.standardUnitsPerBatch,
                }
              : null
          })()
        : null

      // Calculate weekday config for this item to find next prep day
      const weekdayConfig = {
        sunday: prepItem.sunday,
        monday: prepItem.monday,
        tuesday: prepItem.tuesday,
        wednesday: prepItem.wednesday,
        thursday: prepItem.thursday,
        friday: prepItem.friday,
        saturday: prepItem.saturday,
      }
      const daysUntilNextPrep = calculateDaysUntilNextPrep(
        date.clone(),
        weekdayConfig,
      )

      // Calculate today's requirement and total until next prep
      const todayUnitAmount = calculateTodayUnitAmount(
        prepItem,
        nextWeekPrepItemRequirementsData.getNextWeekPrepItemRequirementsByDow
          .nodes,
        date.clone(),
      )

      const totalDaysToPrep = daysUntilNextPrep + restDays
      const totalUnitAmountUntilNextPrep =
        calculateTotalUnitAmountUntilNextPrep(
          prepItem,
          nextWeekPrepItemRequirementsData.getNextWeekPrepItemRequirementsByDow
            .nodes,
          date.clone(),
          totalDaysToPrep,
          prepDetails?.standardUnitsPerBatch || 0,
        )

      return {
        prepItemIdCode: prepItem.prepItemIdCode,
        prepItemName: prepItem.prepItemName,
        prepItemUnit: sampleRequirement?.prepItemUnit || '',
        unitAmountNeeded: todayUnitAmount,
        totalUnitAmountUntilNextPrep,
        prepBatchType: prepDetails?.prepBatchType || '',
        standardUnitsPerBatch: prepDetails?.standardUnitsPerBatch || 0,
        prepOnHandDetails,
        parentPrepCategoryIdCode: category?.parentPrepCategoryIdCode || '',
        onHandUnit,
        restDays,
        minimumUnit,
        // why: no floor here. This panel answers "do you have enough", so a
        // prep cushion turns a surplus into a red shortfall. The floor still
        // applies on the scheduled path, where it sizes a prep the team is
        // doing anyway.
        prepFloorPercent: 0,
      }
    })

    // Sort by category
    return results.sort((a, b) => {
      if (a.parentPrepCategoryIdCode !== b.parentPrepCategoryIdCode) {
        return a.parentPrepCategoryIdCode.localeCompare(
          b.parentPrepCategoryIdCode,
        )
      }
      return a.prepItemName.localeCompare(b.prepItemName)
    })
  }, [
    effectiveWeekdayConfig,
    date,
    nextWeekPrepItemRequirementsData,
    prepItemCategoryData,
    prepItemBatchStandardsData,
    prepItemSettingsData,
    deliveryDayNumbers,
    deliverySafeItemIds,
    redistributionEnabled,
  ])

  return {
    loading:
      weekdayConfigLoading ||
      nextWeekPrepItemRequirementsLoading ||
      prepItemBatchStandardsLoading ||
      prepItemSettingsLoading ||
      prepItemCategoryLoading ||
      nextWeekTotalSalesForecastLoading ||
      prepSettingsByDateLoading,
    dataSource,
    nonPrepDataSource,
    totalSalesForecast,
    weeklySalesForecast,
    prepAmountIncreasePercent: globalAdjustmentPercent,
  }
}

export default usePrepItemWeekdayDataSource
